import { AppThunk } from '@/store'
import { createAction } from '@/store/actions'
import fetch from '@/utilities/fetch'

import {
  BULK_ADD_USERS_ERROR,
  BULK_ADD_USERS_SUCCESS,
  BULK_FINISHED_PROCESSING,
  BULK_RESET,
  BULK_SET_USER_LIST,
  BULK_START_PROCESSING
} from './actionTypes'

//chunk size based on rough default for json bodyParser
const chunkSize = 90_000
const chunkItems = 25
const maxRequests = 2

//this is to signal to cut the async requests, usually used if the user leaves the page
const globals = { cancelRequest: false, resetAfterCancel: false }

const promiseWrapId = (promise: Promise<unknown>, id: string | number) => {
  const newPromise = promise.then(response => {
    return { response, id, promise }
  }) as Promise<any> & { id: string | number }
  newPromise.id = id
  return newPromise
}
// crude chunking based on character length of the object
const createChunks = (list: any[], maxSize: number, chunkItemLimit: number) => {
  return list.reduce(
    (acc, obj) => {
      const size = JSON.stringify(obj).length
      if (acc.currentSize + size > maxSize || acc.chunks.at(-1).length > chunkItemLimit) {
        acc.chunks.push([obj])
        acc.currentSize = size
      } else {
        acc.chunks.at(-1).push(obj)
        acc.currentSize += size
      }
      return acc
    },
    { currentSize: 0, chunks: [[]] }
  ).chunks
}

export const bulkAddUsers = (listOfUsers: unknown[]): AppThunk => {
  return async dispatch => {
    globals.cancelRequest = false
    dispatch(createAction(BULK_RESET, { isProcessingCsv: true }))

    // the requests can be big so we need to chunk it up
    let requests: ReturnType<typeof promiseWrapId>[] = []
    const completedChunks: Record<string | number, boolean> = {}
    const chunks = createChunks(listOfUsers, chunkSize, chunkItems)
    try {
      for (const [i, chunk] of chunks.entries()) {
        if (globals.cancelRequest) {
          if (globals.resetAfterCancel) dispatch(createAction(BULK_RESET, {}))
          globals.resetAfterCancel = false
          return
        }

        requests.push(
          promiseWrapId(
            fetch('/organizations/:organizationId/users/bulk', { method: 'POST', body: { users: chunk } }, {}),
            i
          )
        )

        while (requests.length >= maxRequests) {
          const response = await Promise.race(requests)
          completedChunks[response.id] = true
          requests = requests.filter(r => {
            return r.id !== response.id
          })
          dispatch(createAction(BULK_ADD_USERS_SUCCESS, { response: response.response }))
        }
      }

      const responses = await Promise.all(requests)
      responses.forEach(response => {
        dispatch(createAction(BULK_ADD_USERS_SUCCESS, { response: response.response }))
      })
    } catch {
      //finish waiting for the remaining requests
      for (const request of requests) {
        try {
          const response = await request
          completedChunks[response.id] = true
          dispatch(createAction(BULK_ADD_USERS_SUCCESS, { response: response.response }))
        } catch {}
      }

      // return any unfinished or incomplete chunks as error
      for (const [i, chunk] of chunks.entries()) {
        if (!completedChunks[i]) {
          dispatch(createAction(BULK_ADD_USERS_ERROR, { users: chunk }))
        }
      }
    }
  }
}

export const setUsersToAddList = (usersToAdd?: unknown): AppThunk => {
  return dispatch => dispatch(createAction(BULK_SET_USER_LIST, { usersToAdd }))
}

export const startProcessing = (csvData?: unknown): AppThunk => {
  return dispatch => dispatch(createAction(BULK_START_PROCESSING, { csvData }))
}
export const finishedProcessing = (): AppThunk => {
  return dispatch => dispatch(createAction(BULK_FINISHED_PROCESSING, {}))
}

export const reset = (): AppThunk => {
  return dispatch => dispatch(createAction(BULK_RESET, {}))
}

export const cancelUpload = (reset = false) => {
  globals.cancelRequest = true
  globals.resetAfterCancel = reset
  return () => {}
}
